import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ArtifactCategory, ArtifactType } from '@/config/objects/artifacts';

/**
 * A live artifact effect as the admin endpoints return it (normalized
 * server-side so facilitator/admin-applied effects carry the same shape
 * as real gadget effects). `targetTeam` is present on the event-wide
 * `/effects/active` feed; the per-team feed omits it.
 */
export interface ActiveEffect {
  id: string;
  effectType: ArtifactType;
  startsAt: string;
  expiresAt: string | null;
  isActive: boolean;
  sourceTeam: { id: string; name: string };
  targetTeam?: { id: string; name: string; iconColor: string | null };
  teamArtifact: {
    artifact: {
      name: string;
      artifactType: ArtifactType;
      artifactCategory: ArtifactCategory;
      effectDuration: number | null;
      effectValue: number | null;
    };
  };
}

interface EffectListResponse {
  success: boolean;
  data: ActiveEffect[];
}

/** Query key for a single team's active effects (admin view). */
export function teamEffectsKey(eventId: string, teamId: string) {
  return ['TeamEffects', eventId, teamId] as const;
}

/** Query key for every active effect across an event (Live view). */
export function eventActiveEffectsKey(eventId: string) {
  return ['EventActiveEffects', eventId] as const;
}

/** A single team's currently-active effects (admin/facilitator). */
export function useTeamEffects(eventId: string, teamId: string) {
  const apiClient = useApiClient();
  return useQuery<EffectListResponse>({
    queryKey: teamEffectsKey(eventId, teamId),
    queryFn: () => apiClient.get(`/events/${eventId}/teams/${teamId}/effects`),
    enabled: !!eventId && !!teamId,
  });
}

/** Every active effect across the event, annotated by target team. */
export function useEventActiveEffects(eventId: string) {
  const apiClient = useApiClient();
  return useQuery<EffectListResponse>({
    queryKey: eventActiveEffectsKey(eventId),
    queryFn: () => apiClient.get(`/events/${eventId}/effects/active`),
    enabled: !!eventId,
  });
}

/** Refresh both effect feeds after a change (mutation or socket). */
export function invalidateEffectQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  queryClient.invalidateQueries({ queryKey: eventActiveEffectsKey(eventId) });
  queryClient.invalidateQueries({ queryKey: ['TeamEffects', eventId] });
}

/**
 * Change an active effect's remaining time. `durationMinutes` sets a new
 * window from now; the server broadcasts the change to the target team +
 * facilitators.
 */
export function useUpdateEffect(eventId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ effectId, durationMinutes }: { effectId: string; durationMinutes: number }) =>
      apiClient.patch(`/events/${eventId}/effects/${effectId}`, { durationMinutes }),
    onSuccess: () => invalidateEffectQueries(queryClient, eventId),
  });
}

/** Dismiss (deactivate) an active effect. */
export function useDismissEffect(eventId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ effectId }: { effectId: string }) =>
      apiClient.delete(`/events/${eventId}/effects/${effectId}`),
    onSuccess: () => invalidateEffectQueries(queryClient, eventId),
  });
}
